import { useCallback, useMemo, useState } from "react";
import { useLoader } from "@react-three/fiber";
import {
  Environment,
  OrbitControls,
  PresentationControls,
} from "@react-three/drei";
import { STLLoader } from "three-stdlib";

import { Camera } from "./Camera";
import { Object3D } from "./Object3D";
import { Lights } from "./Lights";
import { type MeshStandardMaterialProperties } from "three";
import type { ModelDimensions } from "../types";
import { MATERIAL_PRESETS } from "../utils";
import { GradientBackground } from "./GradientBackground";

const CAMERA_DISTANCE_FACTOR = 3;
const INITIAL_LATITUDE = Math.PI / 2;
const INITIAL_LONGITUDE = -Math.PI;

interface SceneProps {
  url: string;
  material?: Partial<MeshStandardMaterialProperties>;
}

/**
 * Scene orchestrates:
 * - Model loading
 * - Camera positioning
 * - Lights
 * - Orbit controls
 *
 * Think of this as the "3D environment setup".
 */
export function Scene({ url, material }: SceneProps) {
  const geometry = useLoader(STLLoader, url);

  const [dims, setDims] = useState<ModelDimensions | null>(null);
  const [center, setCenter] = useState<[number, number, number]>([0, 0, 0]);

  const handleLoaded = useCallback((d: ModelDimensions) => {
    setDims(d);
    setCenter([0, 0, d.height / 2]);
  }, []);

  /**
   * Camera distance auto-scales based on model size.
   * This prevents tiny models from being too far
   * and huge models from being clipped.
   */
  const cameraPosition = useMemo(() => {
    if (!dims) return null;

    return {
      latitude: INITIAL_LATITUDE,
      longitude: INITIAL_LONGITUDE,
      distance: dims.boundingRadius * CAMERA_DISTANCE_FACTOR,
    };
  }, [dims]);

  return (
    <>
      <GradientBackground />

      {cameraPosition && (
        <Camera
          initialPosition={cameraPosition}
          center={{
            x: center[0],
            y: center[1],
            z: center[2],
          }}
        />
      )}

      <PresentationControls
        global
        snap={false}
        speed={2}
        rotation={[0, 0, 0]}
        polar={[-Infinity, Infinity]} // Infinite vertical rotation
        azimuth={[-Infinity, Infinity]} // Infinite horizontal rotation
        damping={0}
      >
        <Object3D
          geometry={geometry}
          position={[0, 0, 0]}
          rotation={[0.2, 0.2, 0]}
          materialProps={material ? material : MATERIAL_PRESETS.ZIRCONIA}
          onLoaded={handleLoaded}
        />
      </PresentationControls>

      <OrbitControls
        makeDefault
        enableRotate={false}
        enablePan={false}
        enableZoom
        target={center}
      />

      <Lights />
      <Environment preset="studio" />
    </>
  );
}
